import { reactive, onBeforeUnmount } from 'vue'

let _ctx: CanvasRenderingContext2D | null = null
let _font = '400 14px sans-serif'

function getCtx(): CanvasRenderingContext2D | null {
  if (!_ctx) {
    _ctx = document.createElement('canvas').getContext('2d')
    if (_ctx) _ctx.font = _font
  }
  return _ctx
}

/** 从实际渲染元素同步字体，保证 canvas 测量与页面渲染一致（尤其日文/中文宽度） */
function syncFont(el: HTMLElement) {
  const cs = window.getComputedStyle(el)
  // ctx.font 不支持 line-height，只取 weight + size + family
  const font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`
  if (font !== _font) {
    _font = font
    const ctx = getCtx()
    if (ctx) {
      ctx.font = _font
      if (ctx.font.indexOf('px') === -1) {
        _font = '400 14px sans-serif'
        ctx.font = _font
      }
    }
  }
}

function measureWidth(text: string): number {
  return getCtx()?.measureText(text).width ?? 0
}

function truncateMiddle(name: string, availPx: number): string {
  // +2 容差，避免恰好放得下时被误截
  if (!name || measureWidth(name) <= availPx + 2) return name
  const ellipsis = '...'
  const ellW = measureWidth(ellipsis)
  const half = (availPx - ellW) / 2
  if (half <= 0) return name

  let head = ''
  let headW = 0
  for (const ch of name) {
    const w = measureWidth(ch)
    if (headW + w > half) break
    head += ch
    headW += w
  }

  let tail = ''
  let tailW = 0
  for (let i = name.length - 1; i >= 0; i--) {
    const ch = name[i]
    const w = measureWidth(ch)
    if (tailW + w > half) break
    tail = ch + tail
    tailW += w
  }

  return head + ellipsis + tail
}

/**
 * 提供文件名中间截断能力，自动跟踪容器宽度变化。
 *
 * 两种用法：
 *
 * 1) 文字元素本身宽度稳定（如 flex:1 占满整行）：直接绑定在文字元素上
 *    <span :ref="el => setNameRef(el, uid)" class="file-name">{{ getDisplayName(uid, name) }}</span>
 *
 * 2) 框宽随内容自适应（如聊天附件）：需用「隐藏完整名撑宽 + 绝对定位显示截断」结构，
 *    setNameRef 绑定在外层 .file-confirm-name 上，其宽度由隐藏的完整名决定，保持稳定。
 */
export function useFileNameTruncate() {
  const nameWidths = reactive<Record<string, number>>({})
  const nameRefs = new Map<string, HTMLElement>()
  const ro = new ResizeObserver(entries => {
    for (const entry of entries) {
      const el = entry.target as HTMLElement
      const uid = el.dataset.truncateUid!
      syncFont(el)
      nameWidths[uid] = entry.contentRect.width
    }
  })

  function setNameRef(el: HTMLElement | null, uid: string) {
    if (el) {
      el.dataset.truncateUid = uid
      nameRefs.set(uid, el)
      syncFont(el)
      ro.observe(el)
    } else {
      const old = nameRefs.get(uid)
      if (old) ro.unobserve(old)
      nameRefs.delete(uid)
    }
  }

  function getDisplayName(uid: string, name: string): string {
    const avail = nameWidths[uid]
    if (!avail) return name
    return truncateMiddle(name, avail)
  }

  function isTruncated(uid: string, name: string): boolean {
    return getDisplayName(uid, name) !== name
  }

  onBeforeUnmount(() => ro.disconnect())

  return { setNameRef, getDisplayName, isTruncated }
}
